/**
 * Gamification (`gamification-core` kit) — time-tracker profile card.
 *
 * Derives player state (XP/level/streak/badges) from live local-first
 * `time_entry`/`focus_session`, without new sync columns:
 * `computePlayerFromChronos` is a pure and deterministic reducer → instantaneous
 * feedback (works for visitors without sync). Rewards logging time and
 * completing focus sessions, with streak of worked days. Private leagues by
 * default.
 *
 * Respects `prefers-reduced-motion`: XP bar animates with `transition` which
 * global guard disables; no JS animations.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Flame, Sparkles, Trophy } from "@fleet/icons";
import {
  computePlayerFromChronos,
  levelProgress,
  liveFocusSessionFilters,
  liveTimeEntryFilters,
  queryKeys,
  STREAK_WORK,
  TIME_TRACKER_BADGES,
} from "@time-tracker/shared";
import {
  and,
  eq,
  focus_session,
  type FocusSession,
  sql,
  time_entry,
  type TimeEntry,
} from "@time-tracker/powersync";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@fleet/ui";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";

/** Human title by badge id (reactive i18n). */
function badgeTitle(t: (d: ReturnType<typeof msg>) => string, id: string): string {
  switch (id) {
    case "first-block": {
      return t(msg`First block`);
    }
    case "ten-blocks": {
      return t(msg`Getting going`);
    }
    case "fifty-blocks": {
      return t(msg`Clocked in`);
    }
    case "century-blocks": {
      return t(msg`Marathon`);
    }
    case "first-focus": {
      return t(msg`First focus`);
    }
    case "ten-focus": {
      return t(msg`Deep focus`);
    }
    case "week-streak": {
      return t(msg`Focused week`);
    }
    case "month-streak": {
      return t(msg`Productive month`);
    }
    case "level-5": {
      return t(msg`Level 5`);
    }
    case "level-10": {
      return t(msg`Level 10`);
    }
    default: {
      return id;
    }
  }
}

interface GamificationSectionProps {
  userId: string;
}

export function GamificationSection(props: GamificationSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const entries = createPowerSyncQuery<TimeEntry>({
    queryKey: () => queryKeys.chronos.gamificationEntries(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(time_entry)
        .where(
          and(
            eq(time_entry.user_id, props.userId),
            sql`end_time IS NOT NULL`,
            ...liveTimeEntryFilters(),
          ),
        );
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const sessions = createPowerSyncQuery<FocusSession>({
    queryKey: () => queryKeys.chronos.gamificationFocus(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(focus_session)
        .where(
          and(
            eq(focus_session.user_id, props.userId),
            eq(focus_session.status, "completed"),
            ...liveFocusSessionFilters(),
          ),
        );
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const player = createMemo(
    () => computePlayerFromChronos(entries.data ?? [], sessions.data ?? []).state,
  );
  const progress = createMemo(() => levelProgress(player().xp));
  const streak = createMemo(() => player().streaks[STREAK_WORK]?.current ?? 0);
  const owned = createMemo(() => new Set(player().badges));
  const isPending = () => entries.isPending || sessions.isPending;

  const levelLabel = () => {
    const { level } = progress();
    return t(msg`Level ${level}`);
  };
  const xpLabel = () => {
    const { xp } = player();
    return t(msg`${xp} XP`);
  };
  const streakLabel = () => {
    const days = streak();
    return t(msg`${days} day streak`);
  };
  const toNextLabel = () => {
    const xp = progress().xpToNext;
    return t(msg`${xp} XP to next level`);
  };

  return (
    <Card data-testid="gamification-section">
      <CardHeader>
        <div class="flex items-center gap-2">
          <Sparkles class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Progress`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <Show when={!isPending()} fallback={<Skeleton class="h-24 w-full rounded-xl" />}>
          {/* Nivel + barra de XP */}
          <div class="space-y-2">
            <div class="flex items-end justify-between">
              <div class="flex items-center gap-2">
                <span
                  class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary"
                  data-testid="gamification-level"
                >
                  {progress().level}
                </span>
                <div class="flex flex-col">
                  <span class="text-sm font-semibold text-foreground">{levelLabel()}</span>
                  <span class="text-xs text-muted-foreground" data-testid="gamification-xp">
                    {xpLabel()}
                  </span>
                </div>
              </div>
              <Show when={streak() > 0}>
                <span
                  class="flex items-center gap-1 rounded-full bg-warning/10 px-2.5 py-1 text-xs font-semibold text-warning"
                  data-testid="gamification-streak"
                >
                  <Flame class="h-3.5 w-3.5" />
                  {streakLabel()}
                </span>
              </Show>
            </div>
            <div class="h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                class="h-full rounded-full bg-primary transition-[width] duration-500"
                style={{ width: `${Math.round(progress().ratio * 100)}%` }}
              />
            </div>
            <p class="text-right text-xs text-muted-foreground">{toNextLabel()}</p>
          </div>

          {/* Badges (obtenidos en color, pendientes en gris) */}
          <div>
            <p class="mb-3 flex items-center gap-1.5 text-sm font-medium text-foreground">
              <Trophy class="h-4 w-4 text-muted-foreground" />
              {t(msg`Achievements`)}
            </p>
            <div class="flex flex-wrap gap-2">
              <For each={TIME_TRACKER_BADGES}>
                {(badge) => {
                  const has = () => owned().has(badge.id);
                  return (
                    <Tooltip>
                      <TooltipTrigger
                        as="div"
                        class="flex h-11 w-11 items-center justify-center rounded-xl border transition-colors"
                        classList={{
                          "border-primary/30 bg-primary/10 text-primary": has(),
                          "border-dashed border-muted-foreground/20 bg-muted/40 text-muted-foreground/40":
                            !has(),
                        }}
                        data-testid={`badge-${badge.id}`}
                        data-earned={has() ? "true" : "false"}
                        aria-label={badgeTitle(t, badge.id)}
                      >
                        <Trophy class="h-5 w-5" />
                      </TooltipTrigger>
                      <TooltipContent>{badgeTitle(t, badge.id)}</TooltipContent>
                    </Tooltip>
                  );
                }}
              </For>
            </div>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}
